import { faMap } from "@fortawesome/pro-solid-svg-icons";
import { computed } from "vue";
import { usePropertyBuilder } from "../components/views/config/helpers";
import type { IUseMods } from "../components/views/mods";
import { useSteamWorkshop } from "../components/views/mods/steam_workshop";
import type { CsgoProfileNamespace, SteamWorkshopItem } from "../graphql";
import { useServiceStore } from "../store/service";
import { commonMenuItems } from "./common";
import defaultProfile, { asyncRoute } from "./default";
import { type ConfigSection, type GameProfile, type Menu, MenuItem } from "./types";

const createMenu = (): Menu => {
  const menu = defaultProfile().menu;
  menu[0].items.push(
    commonMenuItems.console,
    new MenuItem(
      "mapgroups",
      faMap,
      asyncRoute(() => import("../components/views/config/csgo/map_groups/MapGroups.vue")),
    ),
  );
  return menu;
};

const configSections: ConfigSection[] = [
  { component: "SteamGslt" },
  { component: "ConfSection", props: { name: "basic" } },
  { component: "CsgoMapSelection" },
  { component: "ConfSection", props: { name: "bot" } },
  { component: "ConfSection", props: { name: "gotv" } },
  { component: "ConfSection", props: { name: "casual_misc" } },
  { component: "ConfSection", props: { name: "casual_money" } },
  { component: "ConfSection", props: { name: "competitive_misc" } },
  { component: "ConfSection", props: { name: "competitive_money" } },
  { component: "ConfSection", props: { name: "armsrace_misc" } },
  { component: "ConfSection", props: { name: "armsrace_gungame" } },
  { component: "ConfSection", props: { name: "demolition_misc" } },
  { component: "ConfSection", props: { name: "demolition_gungame" } },
  { component: "ConfSection", props: { name: "deathmatch_misc" } },
];

function useMods() {
  const service = useServiceStore();
  const { buildConfig } = usePropertyBuilder();

  const profile = computed(() => service.ctx.profile as CsgoProfileNamespace | undefined);
  const selectedWorkshopItems = computed(() => profile.value?.selectedWorkshopItems ?? []);
  const subscribedFileIds = buildConfig<Array<string>>("subscribedFileIds", []);
  const subscribedCollectionIds = buildConfig<Array<string>>("subscribedCollectionIds", []);

  return useSteamWorkshop(selectedWorkshopItems, subscribedFileIds, subscribedCollectionIds);
}

function getAccessData() {
  const service = useServiceStore();
  const server = service.ctx.sys?.gameServer;
  if (service.ctx.profile?.publicConfigs["server"] == null) {
    return { ip: server?.serverIp, port: server?.serverPort };
  }

  const password = service.ctx.profile?.publicConfigs["server"]["sv_password"];
  return { ip: server?.serverIp, port: server?.serverPort, password };
}

function formattedCopyAccessData() {
  const { ip, port, password } = getAccessData();
  const passwordCommand = password ? `;password ${password}` : "";
  return `connect ${ip}:${port}${passwordCommand}`;
}

export default (): Partial<GameProfile<SteamWorkshopItem, IUseMods<SteamWorkshopItem>>> => ({
  menu: createMenu(),
  configSections,
  hasExpertMode: true,
  useMods,
  copyAccessDataFn: formattedCopyAccessData,
});
